import { useCallback } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { SignPetitionInput, SignPetitionResult, SignPetitionTrackingData } from '#src/app/shared/api/signature';
import { createSignature } from '#src/app/shared/api/signature';

import { useTrackingSignData } from './signData/trackingSignData';
import { useUserAgentSignData } from './signData/userAgentSignData';

export type SignPetitionHookInput = Omit<
	SignPetitionInput,
	| 'petitionId'
	| 'pageContext'
	| 'recentlySeenMembershipRequest'
	| keyof ReturnType<typeof useTrackingSignData>
	| keyof ReturnType<typeof useUserAgentSignData>
>;
type HookOptions = {
	/**
	 * enables bandits tracking based on "current_content"
	 */
	bandits?: boolean;
	pageContext: string;
	/**
	 * extra properties for the sign event, merged into the default tracking data
	 */
	trackingData?: Omit<SignPetitionTrackingData, 'webapp_name'>;
};

type Options = {
	signature: SignPetitionHookInput;
};

export function useSignPetition(
	petitionId: string,
	{ bandits, pageContext, trackingData: extraTrackingData }: HookOptions,
): (options: Options) => Promise<SignPetitionResult> {
	const utilityContext = useUtilityContext();

	const trackingSignData = useTrackingSignData({ bandits });
	const userAgentData = useUserAgentSignData();

	return useCallback(
		async ({ signature }) => {
			const result = await createSignature(
				{
					signature: {
						petitionId,
						pageContext,
						...trackingSignData,
						...(extraTrackingData && {
							trackingData: {
								...trackingSignData.trackingData,
								...extraTrackingData,
							},
						}),
						...userAgentData,
						...signature,
					},
				},
				utilityContext,
			);

			return result;
		},
		[petitionId, utilityContext, trackingSignData, extraTrackingData, userAgentData, pageContext],
	);
}
